import { useEffect, useState, type ReactNode } from "react";
import { can } from "@sts/schemas";
import type {
  Construction,
  DesignReadiness,
  DesignReadinessRoom,
  DesignReadinessStorey,
  DesignReadinessWrite,
  Me,
  StoreyCeilingAnswer,
  StoreyFloorAnswer,
  StoreyFloorKind,
} from "../api/types.js";
import { href } from "../app/router.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { Async, Failure } from "./Async.jsx";

/**
 * "Ready for design" — what another portal needs before it can draw a design
 * pack from this survey, and the answers that settle it, in one card.
 *
 * The survey is designed on portal.scannix.co from the handover code, and every
 * gap here is one that portal flags on import: rooms under the roof with no
 * ceiling (RoomPlan captures none — the heat loss is about a fifth low without
 * them), external walls nobody has said the construction of, room areas nobody
 * has confirmed, and the bedroom count hot-water sizing needs. Settle them here
 * and the pack arrives ready. See apps/api/src/survey/design-readiness.ts.
 */
export function DesignReadinessCard({
  surveyId,
  jobId,
  me,
  revision = 0,
  onChanged,
}: {
  readonly surveyId: string;
  readonly jobId: string;
  readonly me: Me;
  /** Bumped when the survey's rooms changed elsewhere on the page. */
  readonly revision?: number;
  /** A write here changed the rooms and the heat loss: the page re-reads both. */
  readonly onChanged: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<DesignReadiness>(
    () => client.designReadiness(surveyId),
    [client, surveyId, revision],
  );
  const [latest, setLatest] = useState<DesignReadiness | null>(null);
  useEffect(() => setLatest(null), [state]);

  const canEdit = can({ role: me.role, resource: "survey", action: "update", isAssigned: true });
  const canDecide = can({ role: me.role, resource: "design", action: "update" });

  const applied = (write: DesignReadinessWrite): void => {
    setLatest(write.readiness);
    onChanged();
  };

  return (
    <section className="design-readiness">
      <h2>Ready for design</h2>
      <p className="muted small">
        What portal.scannix.co needs to draw a design pack from this survey. Settle these before you share the
        handover code — each one it would otherwise flag on import.
      </p>
      <Async state={state} what="what this survey still needs for a design" reload={reload}>
        {(loaded) => {
          const r = latest ?? loaded;
          const editable = canEdit && r.status !== "superseded";
          return (
            <div className="design-readiness__items">
              <Address readiness={r} />
              <Occupancy readiness={r} jobId={jobId} canEdit={canDecide} onSaved={reload} />
              <BuildingParts readiness={r} canEdit={editable} onApplied={applied} />
              {(r.parts ?? []).length === 0 ? (
                r.storeys.map((storey) => (
                  <StoreyFabric key={storey.level} readiness={r} storey={storey} canEdit={editable} onApplied={applied} />
                ))
              ) : (
                <details className="design-readiness__storeys">
                  <summary className="small">Answer storey by storey instead</summary>
                  {r.storeys.map((storey) => (
                    <StoreyFabric key={storey.level} readiness={r} storey={storey} canEdit={editable} onApplied={applied} />
                  ))}
                </details>
              )}
              <Roofs readiness={r} canEdit={editable} onApplied={applied} />
              <ExternalWalls readiness={r} canEdit={editable} onApplied={applied} />
              <RoomAreas readiness={r} canEdit={editable} onApplied={applied} />
            </div>
          );
        }}
      </Async>
    </section>
  );
}

function Item({
  done,
  title,
  children,
}: {
  readonly done: boolean;
  readonly title: string;
  readonly children?: ReactNode;
}): ReactNode {
  return (
    <div className={`design-readiness__item${done ? " is-done" : ""}`}>
      <h3>
        <span aria-hidden="true">{done ? "✓" : "•"}</span> {title}
      </h3>
      {children}
    </div>
  );
}

/** The address the receiving portal keys the property on: no address, no handover. */
function Address({ readiness }: { readonly readiness: DesignReadiness }): ReactNode {
  if (readiness.jobHasAddress) return <Item done title="Address: on the job, ready to hand over" />;
  return (
    <Item done={false} title="Address: the job has no address or postcode yet">
      <p className="muted small">
        portal.scannix.co files the survey against the property&apos;s address, so a handover code for this survey is
        refused until it has one. <a href={href.job(readiness.jobId)}>Add the customer and address on the job</a>.
      </p>
    </Item>
  );
}

const FLOOR_CHOICES: readonly { readonly kind: Exclude<StoreyFloorKind, "mixed" | "none">; readonly label: string }[] = [
  { kind: "solid-ground", label: "Solid ground floor" },
  { kind: "suspended-ground", label: "Suspended timber floor" },
  { kind: "heated-below", label: "Heated room below" },
  { kind: "unheated-below", label: "Unheated space below" },
  { kind: "dwelling-below", label: "Another dwelling below" },
  { kind: "exposed", label: "Outside air below" },
];

const FLOOR_WORDS: Readonly<Record<StoreyFloorKind, string>> = {
  "solid-ground": "solid ground floor",
  "suspended-ground": "suspended timber floor",
  "heated-below": "heated room below",
  "unheated-below": "unheated space below",
  exposed: "outside air below",
  "dwelling-below": "another dwelling below",
  mixed: "differs room to room",
  none: "no floor recorded",
};

/** What one set of floor and roof answers covers, and how it saves. */
interface FabricTarget {
  readonly name: string;
  readonly title: string;
  readonly floor: StoreyFloorKind;
  readonly ceiling: DesignReadinessStorey["ceiling"];
  /** Finishes "Set the floor of …". */
  readonly floorScope: string;
  /** Finishes "Put a roof over …". */
  readonly roofScope: string;
  /** Finishes "Set another dwelling above …". */
  readonly aboveScope: string;
  readonly setFloor: (answer: StoreyFloorAnswer) => Promise<DesignReadinessWrite>;
  readonly setCeiling: (answer: StoreyCeilingAnswer) => Promise<DesignReadinessWrite>;
}

function ceilingWordsOf(ceiling: DesignReadinessStorey["ceiling"]): string {
  return ceiling === "roof"
    ? "roof over every room"
    : ceiling === "heated-above"
      ? "heated room above"
      : ceiling === "dwelling-above"
        ? "another dwelling above"
        : "ceiling differs room to room";
}

/**
 * One storey's floor and ceiling, answered once for every heated room on it —
 * what is under the rooms and what is over them. See survey/storey-fabric.ts.
 */
function StoreyFabric({
  readiness,
  storey,
  canEdit,
  onApplied,
}: {
  readonly readiness: DesignReadiness;
  readonly storey: DesignReadinessStorey;
  readonly canEdit: boolean;
  readonly onApplied: (write: DesignReadinessWrite) => void;
}): ReactNode {
  const { client } = usePortal();
  const words = storey.name.toLowerCase();
  const height = storey.ceilingHeightM !== null ? `ceiling ${storey.ceilingHeightM.toFixed(2)} m from the scan` : "no ceiling height yet";
  return (
    <FabricAnswers
      target={{
        name: storey.name,
        title: `${storey.name} (${storey.rooms} room${storey.rooms === 1 ? "" : "s"}): ${FLOOR_WORDS[storey.floor]}; ${ceilingWordsOf(storey.ceiling)}; ${height}`,
        floor: storey.floor,
        ceiling: storey.ceiling,
        floorScope: `every room on the ${words}`,
        roofScope: "every room",
        aboveScope: `every room on the ${words}`,
        setFloor: (answer) => client.setStoreyFloor(readiness.surveyId, storey.level, answer),
        setCeiling: (answer) => client.setStoreyCeiling(readiness.surveyId, storey.level, answer),
      }}
      canEdit={canEdit}
      onApplied={onApplied}
    />
  );
}

/**
 * The whole property's floor and roof — or, with an extension's rooms ticked,
 * the main building's and the extension's (0058). A floor answer goes to the
 * part's lowest rooms, a roof answer to its highest.
 */
function BuildingParts({
  readiness,
  canEdit,
  onApplied,
}: {
  readonly readiness: DesignReadiness;
  readonly canEdit: boolean;
  readonly onApplied: (write: DesignReadinessWrite) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const parts = readiness.parts ?? [];
  const rooms = readiness.heatedRooms ?? [];
  const marked = readiness.extensionRoomIds ?? [];
  const [picking, setPicking] = useState(false);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set(marked));
  useEffect(() => setTicked(new Set(marked)), [marked.join(",")]);
  if (parts.length === 0) return null;

  const toggle = (id: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const saveExtension = (ids: readonly string[]) =>
    submit(async () => {
      onApplied(await client.setExtensionRooms(readiness.surveyId, ids));
      setPicking(false);
    });

  return (
    <>
      {parts.map((p) => (
        <FabricAnswers
          key={p.part}
          target={{
            name: p.name,
            title: `${p.name} (${p.rooms} room${p.rooms === 1 ? "" : "s"}): ${FLOOR_WORDS[p.floor]}; ${p.ceiling === "roof" ? "roof" : p.ceiling === "heated-above" ? "no roof yet" : ceilingWordsOf(p.ceiling)}`,
            floor: p.floor,
            ceiling: p.ceiling,
            floorScope: p.part === "whole" ? "the whole property" : `the ${p.name.toLowerCase()}`,
            roofScope: p.part === "whole" ? "the whole property" : `the ${p.name.toLowerCase()}`,
            aboveScope: p.part === "whole" ? "the whole property" : `the ${p.name.toLowerCase()}`,
            setFloor: (answer) => client.setPartFloor(readiness.surveyId, p.part, answer),
            setCeiling: (answer) => client.setPartCeiling(readiness.surveyId, p.part, answer),
          }}
          note={`Floor: ${p.floorRooms.join(", ")}. Roof: ${p.roofRooms.join(", ")}.`}
          canEdit={canEdit}
          onApplied={onApplied}
        />
      ))}
      {canEdit && rooms.length > 1 ? (
        <div className="design-readiness__form building-parts" data-testid="building-parts">
          {!picking ? (
            <>
              <span className="small">
                {marked.length > 0
                  ? `Extension: ${rooms.filter((r) => marked.includes(r.roomId)).map((r) => r.name).join(", ")}.`
                  : "One building: the whole property has the same floor and roof."}
              </span>
              <button type="button" onClick={() => setPicking(true)}>
                {marked.length > 0 ? "Change the extension's rooms" : "It has an extension — pick its rooms"}
              </button>
              {marked.length > 0 ? (
                <button type="button" className="floor-plan__link" disabled={busy} onClick={() => void saveExtension([])}>
                  No extension
                </button>
              ) : null}
            </>
          ) : (
            <>
              <span className="small">Tick the rooms in the extension. The rest are the main building.</span>
              <RoomTicks rooms={rooms} ticked={ticked} onToggle={toggle} />
              <button type="button" disabled={busy} onClick={() => void saveExtension([...ticked])}>
                {busy ? "Saving…" : "Save the extension"}
              </button>
              <button type="button" className="floor-plan__link" onClick={() => setPicking(false)}>
                Cancel
              </button>
            </>
          )}
          {error ? <Failure error={error} what="the extension" /> : null}
        </div>
      ) : null}
    </>
  );
}

function FabricAnswers({
  target,
  note,
  canEdit,
  onApplied,
}: {
  readonly target: FabricTarget;
  /** Which rooms the answers go to, in words. */
  readonly note?: string;
  readonly canEdit: boolean;
  readonly onApplied: (write: DesignReadinessWrite) => void;
}): ReactNode {
  const { busy, error, submit } = useSubmit();
  const [floorKind, setFloorKind] = useState<Exclude<StoreyFloorKind, "mixed" | "none"> | "">("");
  // No default for the other side's temperature: MCS publishes none for a
  // neighbouring home or an unheated space, so the designer gives it.
  const [temperature, setTemperature] = useState("");
  const [floorConstruction, setFloorConstruction] = useState("");
  const [roofConstruction, setRoofConstruction] = useState("");
  const [above, setAbove] = useState(false);
  const [aboveTemperature, setAboveTemperature] = useState("");
  const [aboveU, setAboveU] = useState("");
  const typedNumber = (raw: string) => raw.trim() !== "" && Number.isFinite(Number(raw));
  const floors = useConstructions("floor");
  const roofs = useConstructions("roof");

  const floorDone = target.floor !== "mixed" && target.floor !== "none";

  const applyFloor = () =>
    submit(async () => {
      if (floorKind === "") return;
      const answer =
        floorKind === "unheated-below" || floorKind === "dwelling-below"
          ? { kind: floorKind, temperatureC: Number(temperature) }
          : floorKind === "exposed"
            ? { kind: floorKind, constructionId: floorConstruction }
            : { kind: floorKind };
      onApplied(await target.setFloor(answer));
      setFloorKind("");
    });

  return (
    <Item done={floorDone} title={target.title}>
      {note ? <p className="muted small">{note}</p> : null}
      {canEdit ? (
        <>
          <div className="design-readiness__form" role="group" aria-label={`Floor of the ${target.name}`}>
            <span className="small">Floor</span>
            {FLOOR_CHOICES.map((choice) => (
              <button
                key={choice.kind}
                type="button"
                className={`design-readiness__choice${(floorKind || target.floor) === choice.kind ? " is-chosen" : ""}`}
                aria-pressed={(floorKind || target.floor) === choice.kind}
                onClick={() => setFloorKind(choice.kind)}
              >
                {choice.label}
              </button>
            ))}
          </div>
          {floorKind !== "" ? (
            <div className="design-readiness__form">
              {floorKind === "unheated-below" || floorKind === "dwelling-below" ? (
                <label>
                  {floorKind === "dwelling-below" ? "Temperature of the home below (°C)" : "Temperature of the space below (°C)"}{" "}
                  <input
                    type="number"
                    step={0.5}
                    placeholder="e.g. 18"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                  />
                </label>
              ) : null}
              {floorKind === "exposed" ? (
                <label>
                  Floor construction{" "}
                  <select
                    value={floorConstruction}
                    onFocus={floors.want}
                    onPointerDown={floors.want}
                    onChange={(e) => setFloorConstruction(e.target.value)}
                  >
                    <option value="">{floors.list === null ? "Choose… (loading the list)" : "Choose…"}</option>
                    {(floors.list ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <button
                type="button"
                disabled={
                  busy ||
                  (floorKind === "exposed" && floorConstruction === "") ||
                  ((floorKind === "unheated-below" || floorKind === "dwelling-below") && !typedNumber(temperature))
                }
                onClick={() => void applyFloor()}
              >
                {busy ? "Saving…" : `Set the floor of ${target.floorScope}`}
              </button>
            </div>
          ) : null}
          <div className="design-readiness__form" role="group" aria-label={`Ceiling of the ${target.name}`}>
            <span className="small">Ceiling</span>
            <button
              type="button"
              className={`design-readiness__choice${target.ceiling === "heated-above" ? " is-chosen" : ""}`}
              aria-pressed={target.ceiling === "heated-above"}
              disabled={busy}
              onClick={() =>
                void submit(async () =>
                  onApplied(await target.setCeiling({ kind: "heated-above" })),
                )
              }
            >
              Heated room above
            </button>
            <button
              type="button"
              className={`design-readiness__choice${target.ceiling === "dwelling-above" || above ? " is-chosen" : ""}`}
              aria-pressed={target.ceiling === "dwelling-above" || above}
              onClick={() => setAbove(true)}
            >
              Another dwelling above
            </button>
            <label>
              Roof or loft{" "}
              <select
                value={roofConstruction}
                onFocus={roofs.want}
                onPointerDown={roofs.want}
                onChange={(e) => setRoofConstruction(e.target.value)}
              >
                <option value="">{roofs.list === null ? "Choose… (loading the list)" : "Choose…"}</option>
                {(roofs.list ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={busy || roofConstruction === ""}
              onClick={() =>
                void submit(async () =>
                  onApplied(
                    await target.setCeiling({
                      kind: "roof",
                      constructionId: roofConstruction,
                    }),
                  ),
                )
              }
            >
              Put a roof over {target.roofScope}
            </button>
          </div>
          {above ? (
            <div className="design-readiness__form">
              <label>
                Temperature of the home above (°C){" "}
                <input
                  type="number"
                  step={0.5}
                  placeholder="e.g. 18"
                  value={aboveTemperature}
                  onChange={(e) => setAboveTemperature(e.target.value)}
                />
              </label>
              <label>
                Ceiling U-value, if known (W/m²K){" "}
                <input type="number" step={0.01} min={0} value={aboveU} onChange={(e) => setAboveU(e.target.value)} />
              </label>
              <button
                type="button"
                disabled={busy || !typedNumber(aboveTemperature) || (aboveU.trim() !== "" && !typedNumber(aboveU))}
                onClick={() =>
                  void submit(async () => {
                    onApplied(
                      await target.setCeiling({
                        kind: "dwelling-above",
                        temperatureC: Number(aboveTemperature),
                        ...(aboveU.trim() !== "" ? { uValue: Number(aboveU) } : {}),
                      }),
                    );
                    setAbove(false);
                  })
                }
              >
                {busy ? "Saving…" : `Set another dwelling above ${target.aboveScope}`}
              </button>
              <span className="muted small">
                Left blank, the U-value is a placeholder, listed under Unconfirmed assumptions until confirmed.
              </span>
            </div>
          ) : null}
        </>
      ) : null}
      {floors.error ? <Failure error={floors.error} what="the floor constructions" /> : null}
      {roofs.error ? <Failure error={roofs.error} what="the roof constructions" /> : null}
      {error ? <Failure error={error} what={`the ${target.name.toLowerCase()}`} /> : null}
    </Item>
  );
}

/** Bedrooms and occupants — MIS 3005-D §3.8.1(b), the daily hot water demand. */
function Occupancy({
  readiness,
  jobId,
  canEdit,
  onSaved,
}: {
  readonly readiness: DesignReadiness;
  readonly jobId: string;
  readonly canEdit: boolean;
  readonly onSaved: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const o = readiness.occupancy;
  const [bedrooms, setBedrooms] = useState(o.bedrooms === null ? "" : String(o.bedrooms));
  const [occupants, setOccupants] = useState(o.knownOccupants === null ? "" : String(o.knownOccupants));
  const [newBuild, setNewBuild] = useState(o.isNewBuild);
  const done = o.bedrooms !== null;

  const save = () =>
    submit(async () => {
      const beds = bedrooms.trim() === "" ? undefined : Number(bedrooms);
      const people = occupants.trim() === "" ? undefined : Number(occupants);
      await client.updateDesignDecisions(jobId, {
        ...(beds !== undefined ? { bedrooms: beds } : {}),
        ...(people !== undefined ? { knownOccupants: people } : {}),
        isNewBuild: newBuild,
      });
      onSaved();
    });

  return (
    <Item
      done={done}
      title={
        done
          ? `Occupancy: ${o.bedrooms} bedroom${o.bedrooms === 1 ? "" : "s"}${o.knownOccupants !== null ? `, ${o.knownOccupants} occupant${o.knownOccupants === 1 ? "" : "s"}` : ""}`
          : "Occupancy: no bedroom count yet — hot water sizing needs it"
      }
    >
      {canEdit ? (
        <form
          className="design-readiness__form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label>
            Bedrooms{" "}
            <input
              type="number"
              min={0}
              max={20}
              step={1}
              inputMode="numeric"
              value={bedrooms}
              onChange={(e) => setBedrooms(e.target.value)}
            />
          </label>
          <label>
            Occupants (if known){" "}
            <input
              type="number"
              min={1}
              max={30}
              step={1}
              inputMode="numeric"
              value={occupants}
              onChange={(e) => setOccupants(e.target.value)}
            />
          </label>
          <label>
            <input type="checkbox" checked={newBuild} onChange={(e) => setNewBuild(e.target.checked)} /> New build
          </label>
          <button type="submit" disabled={busy || bedrooms.trim() === ""}>
            {busy ? "Saving…" : "Save occupancy"}
          </button>
        </form>
      ) : null}
      {error ? <Failure error={error} what="the occupancy" /> : null}
    </Item>
  );
}

/**
 * The library rows for one element, loaded the first time somebody opens the
 * picker — not when the page opens. The wall library is long, and a Survey
 * page already makes a dozen reads as it opens; the list is only wanted by the
 * person about to choose from it.
 */
function useConstructions(element: "roof" | "wall" | "floor") {
  const { client } = usePortal();
  const [wanted, setWanted] = useState(false);
  const [list, setList] = useState<readonly Construction[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (!wanted || list !== null) return;
    let live = true;
    client.constructions(element).then(
      (rows) => live && setList(rows),
      (cause: unknown) => live && setError(cause),
    );
    return () => {
      live = false;
    };
  }, [client, element, wanted, list]);
  return { list, error, want: () => setWanted(true) };
}

function RoomTicks({
  rooms,
  ticked,
  onToggle,
}: {
  readonly rooms: readonly DesignReadinessRoom[];
  readonly ticked: ReadonlySet<string>;
  readonly onToggle: (roomId: string) => void;
}): ReactNode {
  return (
    <ul className="design-readiness__rooms">
      {rooms.map((room) => (
        <li key={room.roomId}>
          <label>
            <input type="checkbox" checked={ticked.has(room.roomId)} onChange={() => onToggle(room.roomId)} />{" "}
            {room.name} <span className="muted small">({storeyName(room.storeyLevel)})</span>
          </label>
        </li>
      ))}
    </ul>
  );
}

function storeyName(level: number): string {
  if (level === 0) return "ground floor";
  if (level === 1) return "first floor";
  if (level === 2) return "second floor";
  if (level < 0) return "basement";
  return `floor ${level}`;
}

function useTicks(rooms: readonly DesignReadinessRoom[]) {
  const key = rooms.map((r) => r.roomId).join(",");
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(rooms.filter((r) => r.suggested).map((r) => r.roomId)));
  useEffect(() => {
    setTicked(new Set(rooms.filter((r) => r.suggested).map((r) => r.roomId)));
  }, [key]);
  const toggle = (id: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return { ticked, toggle };
}

function Roofs({
  readiness,
  canEdit,
  onApplied,
}: {
  readonly readiness: DesignReadiness;
  readonly canEdit: boolean;
  readonly onApplied: (write: DesignReadinessWrite) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const rooms = readiness.roomsWithoutRoof;
  const { ticked, toggle } = useTicks(rooms);
  const [constructionId, setConstructionId] = useState("");
  const library = useConstructions("roof");

  const added = readiness.roomsWithAddedRoof;
  const removal =
    canEdit && added.length > 0 ? (
      <div className="design-readiness__form">
        <span className="small">
          Roofs added here: {added.map((r) => r.name).join(", ")}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void submit(async () =>
              onApplied(await client.removeRoofs(readiness.surveyId, added.map((r) => r.roomId))),
            )
          }
        >
          {busy ? "Removing…" : `Remove ${added.length === 1 ? "that roof" : `those ${added.length} roofs`}`}
        </button>
      </div>
    ) : null;

  if (rooms.length === 0) {
    return (
      <Item done title="Roofs by room: every heated room under the roof has one">
        {removal}
        {error ? <Failure error={error} what="the roofs" /> : null}
      </Item>
    );
  }
  const chosen = rooms.filter((r) => ticked.has(r.roomId)).map((r) => r.roomId);
  return (
    <Item
      done={false}
      title={`Roofs by room: ${rooms.length} room${rooms.length === 1 ? "" : "s"} with no roof or ceiling (RoomPlan captures none)`}
    >
      <p className="muted small">
        For a roof over only some rooms — a single-storey extension, a flat-roofed kitchen. Rooms on the top floor
        are ticked; a ceiling between two heated floors loses nothing and needs no entry. For a whole floor, use its
        Ceiling above.
      </p>
      {removal}
      <RoomTicks rooms={rooms} ticked={ticked} onToggle={toggle} />
      {canEdit ? (
        <div className="design-readiness__form">
          <label>
            Roof construction{" "}
            <select
              value={constructionId}
              onFocus={library.want}
              onPointerDown={library.want}
              onChange={(e) => setConstructionId(e.target.value)}
            >
              <option value="">{library.list === null ? "Choose… (loading the list)" : "Choose…"}</option>
              {(library.list ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy || chosen.length === 0 || constructionId === ""}
            onClick={() => void submit(async () => onApplied(await client.addRoofs(readiness.surveyId, chosen, constructionId)))}
          >
            {busy ? "Adding…" : `Add roof to ${chosen.length} room${chosen.length === 1 ? "" : "s"}`}
          </button>
        </div>
      ) : null}
      {library.error ? <Failure error={library.error} what="the roof constructions" /> : null}
      {error ? <Failure error={error} what="the roofs" /> : null}
    </Item>
  );
}

function ExternalWalls({
  readiness,
  canEdit,
  onApplied,
}: {
  readonly readiness: DesignReadiness;
  readonly canEdit: boolean;
  readonly onApplied: (write: DesignReadinessWrite) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const walls = readiness.externalWalls;
  const [constructionId, setConstructionId] = useState("");
  const library = useConstructions("wall");

  if (walls.total === 0) return <Item done title="External walls: none on this survey" />;
  if (walls.unconfirmed === 0) {
    return <Item done title={`External walls: all ${walls.total} have a confirmed construction or U-value`} />;
  }
  return (
    <Item
      done={false}
      title={`External walls: ${walls.unconfirmed} of ${walls.total} not confirmed${walls.withoutConstruction > 0 ? ` (${walls.withoutConstruction} with no construction at all)` : ""}`}
    >
      <p className="muted small">
        One pick sets every unconfirmed external wall. Walls somebody already confirmed are left as they are; a
        different wall can still be changed on its own under Unconfirmed assumptions.
      </p>
      {canEdit ? (
        <div className="design-readiness__form">
          <label>
            Wall construction{" "}
            <select
              value={constructionId}
              onFocus={library.want}
              onPointerDown={library.want}
              onChange={(e) => setConstructionId(e.target.value)}
            >
              <option value="">{library.list === null ? "Choose… (loading the list)" : "Choose…"}</option>
              {(library.list ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy || constructionId === ""}
            onClick={() =>
              void submit(async () => onApplied(await client.applyExternalWallConstruction(readiness.surveyId, constructionId)))
            }
          >
            {busy ? "Applying…" : `Apply to ${walls.unconfirmed} wall${walls.unconfirmed === 1 ? "" : "s"}`}
          </button>
        </div>
      ) : null}
      {library.error ? <Failure error={library.error} what="the wall constructions" /> : null}
      {error ? <Failure error={error} what="the wall construction" /> : null}
    </Item>
  );
}

function RoomAreas({
  readiness,
  canEdit,
  onApplied,
}: {
  readonly readiness: DesignReadiness;
  readonly canEdit: boolean;
  readonly onApplied: (write: DesignReadinessWrite) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const rooms = readiness.unconfirmedAreas;
  if (rooms.length === 0) return <Item done title="Room areas: all confirmed" />;
  return (
    <Item done={false} title={`Room areas: ${rooms.length} not confirmed by a person`}>
      <ul className="design-readiness__rooms">
        {rooms.map((room) => (
          <li key={room.roomId}>
            {room.name} — {room.floorAreaM2.toFixed(2)} m² <span className="muted small">({room.provenance})</span>
          </li>
        ))}
      </ul>
      {canEdit ? (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void submit(async () =>
              onApplied(await client.confirmFloorAreas(readiness.surveyId, rooms.map((room) => room.roomId))),
            )
          }
        >
          {busy ? "Confirming…" : `Confirm ${rooms.length === 1 ? "this area" : `these ${rooms.length} areas`}`}
        </button>
      ) : null}
      {error ? <Failure error={error} what="the room areas" /> : null}
    </Item>
  );
}
